// 산재보험료 20% 환급 — 공개 계산기 + 사업장 준비 상태 API
import { api } from './api'

export interface IrIndustry {
  key: string
  label: string
  rate_code: string
  permille: number
  rate_pct: number
}

export interface IrLegalNotice { ko: string; en: string }

export interface IrDiscount {
  risk_assessment_pct: number
  risk_assessment_years: number
  owner_education_pct: number
  owner_education_years: number
  combine_rule: string
}

export interface IrReviewCriterion {
  key: string; weight: number; title_ko: string; title_en: string
}

export interface IrMeta {
  industries: IrIndustry[]
  discount: IrDiscount
  worker_limit: number
  notice_id: string
  effective_from: string
  default_monthly_wage: number
  review_criteria: IrReviewCriterion[]
  legal_notice: IrLegalNotice
}

export interface IrTrack {
  discount_pct: number; years: number; savings_annual: number; savings_total: number
}

export interface IrEstimate {
  industry_key: string
  industry_label: string
  rate_permille: number
  rate_pct: number
  worker_count: number
  monthly_wage_per_capita: number
  annual_payroll: number
  annual_premium: number
  tracks: { risk_assessment: IrTrack; owner_education: IrTrack }
  headline_savings: number
  headline_discount_pct: number
  headline_years: number
  legal_notice?: IrLegalNotice
}

export interface IrEstimateIn {
  industry_key: string
  worker_count: number
  monthly_wage_per_capita: number
}

// ── 사업장 준비 상태 ──────────────────────────────────────────────────────────
export interface IrEligibility {
  eligible: boolean
  worker_count: number
  size_ok: boolean
  industry_ok: boolean
  industry_group: string | null
  suggested_industry_key: string | null
  reason: string
}

export interface IrRoadmapStep {
  key: string
  track: string
  auto: boolean
  /** 상시 반복 단계(위험성평가·근로자참여·안전일지) — 1건으로 완료되지 않음, 버튼 항상 노출 */
  ongoing?: boolean
  deeplink: string | null
  title_ko: string
  title_en: string
  done: boolean
  manual: boolean
  note?: string | null
  done_at?: string | null
  detail?: { hint?: string; evidence?: IrEvidence; count?: number; total?: number }
}

export interface IrSignal {
  key: string; weight: number; title_ko: string; title_en: string
  status: 'green' | 'yellow' | 'red' | 'neutral'
  note_ko: string; note_en: string
}

export interface IrEvidence {
  count: number; span_months: number
  earliest: string | null; latest: string | null
  mature: boolean; need_count: number; need_span_months: number
}

export interface IrGateUnmet { code: string; message: string }

export interface IrPackageGate {
  allowed: boolean
  is_paid: boolean
  tenure_months: number
  tenure_ok: boolean
  tenure_need_months: number
  paid_invoice_count: number
  evidence: IrEvidence
  evidence_ok: boolean
  unmet: IrGateUnmet[]
}

export interface IrCalcSnapshot {
  industry_key?: string
  industry_label?: string
  worker_count?: number
  monthly_wage_per_capita?: number
  rate_pct?: number
  annual_premium?: number
  headline_savings?: number
  headline_discount_pct?: number
  headline_years?: number
  saved_at?: string
}

export interface IrRecognition {
  granted_at: string
  next_review_at: string
  days_until_review: number
  valid_until: string
  days_until_rerecognition: number
  savings_total: number | null
  discount_pct: number
}

export interface IrWorkerContext {
  foreign_count: number
  has_foreign: boolean
  senior_count: number
}

export interface IrEducationContext {
  session_count: number
  attendee_total: number
  latest_at: string | null
}

export interface IrSiteState {
  eligibility: IrEligibility
  calc_snapshot: IrCalcSnapshot | null
  calc_preview?: (IrEstimate & { is_preview?: boolean }) | null
  recognition?: IrRecognition | null
  worker_context?: IrWorkerContext
  education_context?: IrEducationContext
  roadmap: { steps: IrRoadmapStep[]; done_count: number; total: number; percent: number }
  signals: IrSignal[]
  package_gate: IrPackageGate
  is_paid: boolean
  legal_notice: IrLegalNotice
  site: { id: string; name: string; ksic_name: string | null; ksic_category: string | null }
}

// ── 공개 계산기 ───────────────────────────────────────────────────────────────
export async function irMeta(): Promise<IrMeta> {
  const { data } = await api.get<IrMeta>('/insurance-refund/meta')
  return data
}

export async function irEstimatePublic(body: IrEstimateIn): Promise<IrEstimate> {
  const { data } = await api.post<IrEstimate>('/insurance-refund/estimate', body)
  return data
}

// ── 사업장 ────────────────────────────────────────────────────────────────────
export async function irSiteState(siteId: string): Promise<IrSiteState> {
  const { data } = await api.get<IrSiteState>(`/sites/${siteId}/insurance-refund`)
  return data
}

export async function irEstimateSite(siteId: string, body: IrEstimateIn): Promise<IrEstimate> {
  const { data } = await api.post<IrEstimate>(`/sites/${siteId}/insurance-refund/estimate`, body)
  return data
}

export async function irMarkChecklist(
  siteId: string, stepKey: string, done: boolean, note?: string,
): Promise<void> {
  await api.post(`/sites/${siteId}/insurance-refund/checklist`, { step_key: stepKey, done, note })
}

/** 인정 신청패키지 PDF. 게이트 미충족(403)이면 unmet 을 담은 에러를 throw. */
export async function irDownloadPackage(siteId: string): Promise<Blob> {
  const res = await api.post(`/sites/${siteId}/insurance-refund/package`, {}, { responseType: 'blob' })
  return res.data as Blob
}

/** 위험성평가 실시규정 PDF — 인정 '사전준비' 필수문서. 패키지와 달리 게이트 없음(무료=워터마크). */
export async function irDownloadRegulation(siteId: string): Promise<Blob> {
  const res = await api.post(`/sites/${siteId}/insurance-refund/regulation`, {}, { responseType: 'blob' })
  return res.data as Blob
}

// ── 패키지에 포함할 증빙 기록 선택 ────────────────────────────────────────────
export interface IrRecordItem {
  id: string
  label: string
  date: string
  status?: string
  meta?: string
  attendees?: number
}

export type IrSelectionKey = 'risk_assessments' | 'tbms' | 'reports' | 'sessions'
export type IrSelection = Record<IrSelectionKey, string[]>

export interface IrRecords {
  risk_assessments: IrRecordItem[]
  tbms: IrRecordItem[]
  reports: IrRecordItem[]
  sessions: IrRecordItem[]
  selected: IrSelection
}

export async function irRecords(siteId: string): Promise<IrRecords> {
  const { data } = await api.get<IrRecords>(`/sites/${siteId}/insurance-refund/records`)
  return data
}

export async function irSaveSelection(siteId: string, selection: IrSelection): Promise<void> {
  await api.post(`/sites/${siteId}/insurance-refund/records/selection`, selection)
}

// ── 다국어 안전보건표지 현장 사진 ─────────────────────────────────────────────
export interface IrSignPhoto {
  id: string
  caption: string | null
  mime: string | null
  created_at: string | null
}

export async function irSignPhotos(siteId: string): Promise<IrSignPhoto[]> {
  const { data } = await api.get<{ items: IrSignPhoto[] }>(`/sites/${siteId}/insurance-refund/sign-photos`)
  return data.items ?? []
}

export async function irUploadSignPhoto(siteId: string, file: File, caption?: string): Promise<IrSignPhoto> {
  const fd = new FormData()
  fd.append('photo', file)
  if (caption) fd.append('caption', caption)
  const { data } = await api.post<IrSignPhoto>(`/sites/${siteId}/insurance-refund/sign-photos`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function irDeleteSignPhoto(siteId: string, photoId: string): Promise<void> {
  await api.delete(`/sites/${siteId}/insurance-refund/sign-photos/${photoId}`)
}

/** 사진은 인증이 필요해 blob 으로 받아 objectURL 로 표시한다. */
export async function irSignPhotoBlob(siteId: string, photoId: string): Promise<Blob> {
  const res = await api.get(`/sites/${siteId}/insurance-refund/sign-photos/${photoId}/image`, { responseType: 'blob' })
  return res.data as Blob
}
